import { Schema } from "effect";
import { Currency } from "../shared";
export const StripeObject = Schema.Struct({
    id: Schema.String,
    object: Schema.Union(Schema.Literal("event"), Schema.Literal("invoice"), Schema.Literal("payment_intent"), Schema.Literal("subscription"), Schema.Literal("checkout.session"), Schema.Literal("charge"))
});
export const StripeInvoiceStatus = Schema.Literal("draft", "open", "paid", "void", "uncollectible");
export const StripeInvoiceType = Schema.Struct({
    id: Schema.NullOr(Schema.String),
    object: Schema.Literal("invoice"),
    auto_advance: Schema.Boolean,
    automatic_tax: Schema.Any,
    charge: Schema.NullOr(Schema.String),
    collection_method: Schema.Literal("charge_automatically", "send_invoice"),
    currency: Schema.String.pipe(Schema.annotations({
        description: "Three-letter ISO currency code, in lowercase"
    })),
    billing_reason: Schema.NullishOr(Schema.Literal("manual", "subscription", "subscription_update", "subscription_create", "subscription_cycle", "subscription_threshold", "upcoming")),
    customer: Schema.String.pipe(Schema.annotations({
        description: "The ID of the customer who will be billed"
    })),
    description: Schema.NullOr(Schema.String),
    hosted_invoice_url: Schema.NullOr(Schema.String),
    // TODO: Object should be typed
    lines: Schema.Any,
    // TODO: Object should be typed
    metadata: Schema.NullOr(Schema.Any),
    payment_intent: Schema.NullOr(Schema.String),
    period_end: Schema.Number,
    period_start: Schema.Number,
    status: Schema.NullOr(StripeInvoiceStatus),
    subscription: Schema.NullOr(Schema.String),
    total: Schema.Number,
    amount_due: Schema.Number.pipe(Schema.annotations({
        description: "The amount due for the invoice"
    })),
    amount_paid: Schema.Number.pipe(Schema.annotations({
        description: "The amount paid for the invoice"
    })),
    amount_remaining: Schema.Number.pipe(Schema.annotations({
        description: "The amount remaining for the invoice"
    })),
    customer_email: Schema.String.pipe(Schema.annotations({
        description: "The email of the customer"
    }))
});
export const StripeBalanceTransaction = Schema.Struct({
    id: Schema.String,
    object: Schema.Literal("balance_transaction"),
    amount: Schema.Number.pipe(Schema.annotations({
        description: "Amount earned or spent"
    })),
    available_on: Schema.Number.pipe(Schema.annotations({
        description: "Time at which this balance transaction will become available"
    })),
    created: Schema.Number.pipe(Schema.annotations({
        description: "Time at which the object was created. Measured in seconds since the Unix epoch."
    })),
    currency: Schema.String.pipe(Schema.annotations({
        description: "Three-letter ISO currency code, in lowercase. Must be a supported currency."
    })),
    description: Schema.NullOr(Schema.String).pipe(Schema.annotations({
        description: "An arbitrary string attached to the object. Often useful for displaying to users."
    })),
    exchange_rate: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "The exchange rate used to convert this balance to the charge currency."
    })),
    fee: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "The amount of the fee that was taken when Stripe processed this balance transaction."
    })),
    fee_details: Schema.NullOr(Schema.Array(Schema.Struct({
        type: Schema.Union(Schema.Literal("application_fee"), Schema.Literal("stripe_fee"), Schema.Literal("tax"), Schema.Literal("payment_method_passthrough_fee"), Schema.Literal("withheld_tax")),
        amount: Schema.Number,
        application: Schema.NullOr(Schema.String),
        currency: Schema.String,
        description: Schema.NullOr(Schema.String)
    }))).pipe(Schema.annotations({
        description: "A list of all of the fees that were collected when processing this balance transaction."
    })),
    net: Schema.Number.pipe(Schema.annotations({
        description: "The amount of the net balance (the amount that is left on the card)."
    })),
    status: Schema.Union(Schema.Literal("pending"), Schema.Literal("available")).pipe(Schema.annotations({
        description: "Whether the balance transaction is pending or has succeeded."
    })),
    type: Schema.String
});
export const StripePaymentIntentType = Schema.Struct({
    id: Schema.String,
    object: Schema.Literal("payment_intent"),
    amount: Schema.Number.pipe(Schema.annotations({
        description: "Amount intended to be collected by this payment"
    })),
    currency: Schema.String.pipe(Schema.annotations({
        description: "Three-letter ISO currency code, in lowercase"
    })),
    customer: Schema.NullOr(Schema.String).pipe(Schema.annotations({
        description: "ID of the Customer this payment intent belongs to"
    })),
    created: Schema.Number.pipe(Schema.annotations({
        description: "Time at which the payment intent was created"
    })),
    status: Schema.Literal("requires_payment_method", "requires_confirmation", "requires_action", "processing", "requires_capture", "canceled", "succeeded").pipe(Schema.annotations({
        description: "Status of this payment intent (succeeded, processing, canceled, requires_payment_method, etc)"
    })),
    // Nullable in the API, but we can assume it's always there
    invoice: Schema.String.pipe(Schema.annotations({
        description: "The ID of the invoice this payment intent is for"
    })),
    cancellation_reason: Schema.NullOr(Schema.Literal("duplicate", "fraudulent", "requested_by_customer", "abandoned", "failed_invoice", "void_invoice", "automatic")).pipe(Schema.annotations({
        description: "Reason for cancellation of this payment intent if status is canceled"
    })),
    last_payment_error: Schema.NullOr(Schema.Struct({
        code: Schema.String,
        message: Schema.String,
        type: Schema.String
    })).pipe(Schema.annotations({
        description: "Details about the payment error if status indicates failure"
    }))
});
export const StripeChargeRefundType = Schema.Struct({
    id: Schema.String,
    object: Schema.Literal("charge"),
    amount: Schema.Number.pipe(Schema.annotations({
        description: "Amount intended to be collected by this payment"
    })),
    amount_refunded: Schema.Number.pipe(Schema.annotations({
        description: "Amount intended to be collected by this payment"
    })),
    amount_captured: Schema.Number.pipe(Schema.annotations({
        description: "Amount intended to be collected by this payment"
    })),
    currency: Schema.String.pipe(Schema.annotations({
        description: "Three-letter ISO currency code, in lowercase"
    })),
    customer: Schema.NullOr(Schema.String).pipe(Schema.annotations({
        description: "ID of the Customer this payment intent belongs to"
    })),
    created: Schema.Number.pipe(Schema.annotations({
        description: "Time at which the charge was created"
    })),
    // Nullable in the API, but we can assume it's always there
    invoice: Schema.String.pipe(Schema.annotations({
        description: "The ID of the invoice this charge is for"
    }))
});
export const StripeCancellationFeedback = Schema.Literal("customer_service", "low_quality", "missing_features", "other", "switched_service", "too_complex", "too_expensive", "unused");
export const StripeCancellationReason = Schema.NullOr(Schema.Literal("cancellation_requested", "payment_disputed", "payment_failed")).pipe(Schema.annotations({
    description: "Why this subscription was canceled."
}));
export const StripeSubscriptionStatus = Schema.Literal("incomplete", "incomplete_expired", "trialing", "active", "past_due", "canceled", "unpaid", "paused");
export const StripeSubscriptionSchedulePhase = Schema.Struct({
    billing_cycle_anchor: Schema.Literal("automatic", "phase_start"),
    start_date: Schema.Number,
    end_date: Schema.Number,
    trial_end: Schema.Number
});
// Stripe returns currency codes in lowercase, but we want them in uppercase
export const PreprocessedCurrencySchema = Currency.Code.ISO4217.FromLowercase;
export const StripeSubscriptionItemType = Schema.Struct({
    id: Schema.String,
    object: Schema.String,
    billing_thresholds: Schema.NullOr(Schema.Any),
    created: Schema.Number,
    plan: Schema.Struct({
        id: Schema.String,
        object: Schema.String,
        active: Schema.Boolean,
        amount: Schema.Number,
        amount_decimal: Schema.String,
        billing_scheme: Schema.String,
        created: Schema.Number,
        currency: PreprocessedCurrencySchema,
        discounts: Schema.NullOr(Schema.Any),
        interval: Schema.String,
        interval_count: Schema.Number,
        livemode: Schema.Boolean,
        product: Schema.String,
        trial_period_days: Schema.NullOr(Schema.Number)
    }),
    price: Schema.Struct({
        id: Schema.String,
        object: Schema.String,
        active: Schema.Boolean,
        billing_scheme: Schema.String,
        created: Schema.Number,
        currency: Schema.String,
        custom_unit_amount: Schema.NullOr(Schema.Any),
        livemode: Schema.Boolean,
        lookup_key: Schema.NullOr(Schema.String),
        product: Schema.String,
        recurring: Schema.Struct({
            interval: Schema.String,
            interval_count: Schema.Number,
            trial_period_days: Schema.NullOr(Schema.Number)
        }),
        tax_behavior: Schema.String,
        type: Schema.String,
        unit_amount: Schema.Number,
        unit_amount_decimal: Schema.String
    }),
    quantity: Schema.Number,
    subscription: Schema.String,
    tax_rates: Schema.Array(Schema.String)
});
export const StripeSubscriptionType = Schema.Struct({
    id: Schema.String,
    object: Schema.Literal("subscription"),
    application_fee_percent: Schema.NullOr(Schema.Number.pipe(Schema.annotations({
        description: "A non-negative decimal between 0 and 100, with at most two decimal places. This represents the percentage of the subscription invoice total that will be transferred to the application owner's Stripe account."
    }))),
    cancel_at: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "A date in the future at which the subscription will automatically get canceled"
    })),
    cancel_at_period_end: Schema.Boolean.pipe(Schema.annotations({
        description: "Whether this subscription will (if status=active) or did (if status=canceled) cancel at the end of the current billing period."
    })),
    canceled_at: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "If the subscription has been canceled, the date of that cancellation. If the subscription was canceled with cancel_at_period_end, canceled_at will reflect the time of the most recent update request, not the end of the subscription period when the subscription is automatically moved to a canceled state."
    })),
    cancellation_details: Schema.NullOr(Schema.Struct({
        comment: Schema.NullOr(Schema.String),
        feedback: Schema.NullOr(StripeCancellationFeedback),
        reason: StripeCancellationReason
    }).pipe(Schema.annotations({
        description: "Details about why this subscription was cancelled"
    }))),
    created: Schema.Number.pipe(Schema.annotations({
        description: "Time at which the object was created. Measured in seconds since the Unix epoch."
    })),
    currency: PreprocessedCurrencySchema.pipe(Schema.annotations({
        description: "Three-letter ISO currency code, in lowercase. Must be a supported currency."
    })),
    current_period_end: Schema.Number.pipe(Schema.annotations({
        description: "End of the current period that the subscription has been invoiced for. At the end of this period, a new invoice will be created."
    })),
    current_period_start: Schema.Number.pipe(Schema.annotations({
        description: "Start of the current period that the subscription has been invoiced for."
    })),
    customer: Schema.String.pipe(Schema.annotations({
        description: "ID of the customer who owns the subscription."
    })),
    days_until_due: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "Number of days a customer has to pay invoices generated by this subscription. This value will be null for subscriptions where collection_method=charge_automatically."
    })),
    default_payment_method: Schema.NullOr(Schema.String).pipe(Schema.annotations({
        description: "ID of the default payment method for the subscription. It must belong to the customer associated with the subscription. This takes precedence over default_source."
    })),
    default_source: Schema.NullOr(Schema.String).pipe(Schema.annotations({
        description: "ID of the default payment source for the subscription. It must belong to the customer associated with the subscription."
    })),
    default_tax_rates: Schema.Array(Schema.String).pipe(Schema.annotations({
        description: "The tax rates that will apply to any subscription item that does not have tax_rates set."
    })),
    description: Schema.NullOr(Schema.String).pipe(Schema.annotations({
        description: "The subscription's description, meant to be displayable to the customer."
    })),
    discount: Schema.NullOr(Schema.Any).pipe(Schema.annotations({
        description: "Describes the current discount applied to this subscription, if there is one."
    })),
    discounts: Schema.NullOr(Schema.Any).pipe(Schema.annotations({
        description: "The discounts applied to the subscription."
    })),
    ended_at: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "If the subscription has ended, the date the subscription ended."
    })),
    items: Schema.Struct({
        object: Schema.String,
        data: Schema.Array(StripeSubscriptionItemType),
        has_more: Schema.Boolean,
        total_count: Schema.Number,
        url: Schema.String
    }).pipe(Schema.annotations({
        description: "List of subscription items, each with an attached price."
    })),
    latest_invoice: Schema.String.pipe(Schema.annotations({
        description: "The most recent invoice this subscription has generated."
    })),
    livemode: Schema.Boolean.pipe(Schema.annotations({
        description: "Whether the subscription is in live mode or test mode"
    })),
    schedule: Schema.NullOr(Schema.Struct({
        current_phase: Schema.Struct({
            end_date: Schema.Number,
            start_date: Schema.Number
        }),
        phases: Schema.Array(StripeSubscriptionSchedulePhase),
        object: Schema.String
    })).pipe(Schema.annotations({
        description: "The schedule attached to the subscription"
    })),
    start_date: Schema.Number.pipe(Schema.annotations({
        description: "Date when the subscription was first created. The date might differ from the created date due to backdating."
    })),
    status: StripeSubscriptionStatus.pipe(Schema.annotations({
        description: "Possible values are incomplete, incomplete_expired, trialing, active, past_due, canceled, unpaid, or paused."
    })),
    transfer_data: Schema.NullOr(Schema.Any).pipe(Schema.annotations({
        description: "The account (if any) the subscription's payments will be attributed to for tax reporting"
    })),
    trial_end: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "If the subscription has a trial, the end of that trial."
    })),
    trial_settings: Schema.Struct({
        end_behavior: Schema.Struct({
            missing_payment_method: Schema.String
        })
    }).pipe(Schema.annotations({
        description: "Settings related to subscription trials."
    })),
    trial_start: Schema.NullOr(Schema.Number).pipe(Schema.annotations({
        description: "If the subscription has a trial, the beginning of that trial."
    }))
});
export const StripeCheckoutSessionType = StripeObject.pipe(Schema.extend(Schema.Struct({
    subscription: Schema.String
})));
export const StripeCustomerSubscriptionCreatedEvent = Schema.Literal("customer.subscription.created");
export const StripeCustomerSubscriptionUpdatedEvent = Schema.Literal("customer.subscription.updated");
export const StripeCustomerSubscriptionDeletedEvent = Schema.Literal("customer.subscription.deleted");
export const StripeCustomerSubscriptionPausedEvent = Schema.Literal("customer.subscription.paused");
export const StripeCustomerSubscriptionResumedEvent = Schema.Literal("customer.subscription.resumed");
export const StripeCustomerSubscriptionPendingUpdateAppliedEvent = Schema.Literal("customer.subscription.pending_update_applied");
export const StripeCustomerSubscriptionPendingUpdateExpiredEvent = Schema.Literal("customer.subscription.pending_update_expired");
export const StripeCustomerSubscriptionTrialWillEndEvent = Schema.Literal("customer.subscription.trial_will_end");
export const StripeInvoicePaymentFailedEvent = Schema.Literal("invoice.payment_failed");
export const StripeInvoicePaymentSucceededEvent = Schema.Literal("invoice.payment_succeeded");
export const StripeInvoiceMarkedUncollectibleEvent = Schema.Literal("invoice.marked_uncollectible");
export const StripePaymentIntentSucceededEvent = Schema.Literal("payment_intent.succeeded");
export const StripePaymentIntentPaymentFailedEvent = Schema.Literal("payment_intent.payment_failed");
export const StripePaymentIntentCanceledEvent = Schema.Literal("payment_intent.canceled");
export const StripeChargeRefundedEvent = Schema.Literal("charge.refunded");
export const AllowedStripeEventType = Schema.Union(// StripeCustomerSubscriptionCreatedEvent,
StripeCustomerSubscriptionUpdatedEvent, StripeCustomerSubscriptionDeletedEvent, StripeCustomerSubscriptionPausedEvent, StripeCustomerSubscriptionResumedEvent, // StripeCustomerSubscriptionTrialWillEndEvent,
// StripeInvoiceMarkedUncollectibleEvent,
StripeInvoicePaymentFailedEvent, StripeInvoicePaymentSucceededEvent, // StripePaymentIntentSucceededEvent,
// StripePaymentIntentPaymentFailedEvent,
// StripePaymentIntentCanceledEvent,,
StripeChargeRefundedEvent);
export const BaseStripeEvent = Schema.Struct({
    id: Schema.String.annotations({
        description: "The ID of the event"
    }),
    object: Schema.Literal("event"),
    api_version: Schema.String.annotations({
        description: "The API version used to generate the event"
    }),
    created: Schema.Number.annotations({
        description: "The timestamp of the event in seconds since the Unix epoch"
    }),
    data: Schema.Struct({
        // FIXME: This seems to be typed wrongly in the Stripe API. Since the api specs don't match what the actual api returns.
        previous_attributes: Schema.Any,
        // previous_attributes: Schema.optional(
        //   Schema.Struct({
        //     plan: Schema.Any,
        //     items: Schema.Struct({
        //       data: Schema.Array(
        //         Schema.Union(
        //           Schema.partial(StripeSubscriptionType),
        //           Schema.partial(StripeInvoiceType),
        //           Schema.partial(StripePaymentIntentType)
        //         ).annotations({
        //           description:
        //             "Received in all *.updated webhooks in case of a change, contains previous values of the fields that changed",
        //         })
        //       ),
        //     }),
        //   })
        // ),
        object: Schema.Union(StripeSubscriptionType, StripeInvoiceType, StripePaymentIntentType, StripeChargeRefundType)
    }),
    request: Schema.NullishOr(Schema.Struct({
        id: Schema.NullOr(Schema.String),
        idempotency_key: Schema.NullOr(Schema.String)
    }).annotations({
        description: "The request that generated the event"
    })),
    type: AllowedStripeEventType,
    livemode: Schema.Boolean
});
export const StripeDeletedCustomer = Schema.Struct({
    id: Schema.String,
    object: Schema.Literal("customer"),
    deleted: Schema.Literal(true)
});
export const StripeCustomer = Schema.Struct({
    id: Schema.String,
    object: Schema.Literal("customer"),
    // TODO: Type this
    address: Schema.NullishOr(Schema.Any),
    // TODO: Type this
    tax: Schema.NullishOr(Schema.Any)
});
export const PreProcessedSubscriptionType = StripeSubscriptionType.omit("customer").pipe(Schema.extend(Schema.Struct({
    taxRate: Schema.Number,
    customer: Schema.Union(StripeCustomer, StripeDeletedCustomer),
    latestInvoice: StripeInvoiceType,
    originalInvoice: StripeInvoiceType,
    originalInvoiceIntent: Schema.Struct({
        balanceTransaction: Schema.NullOr(StripeBalanceTransaction),
        refunded: Schema.Boolean
    }),
    currentIntent: Schema.Struct({
        balanceTransaction: Schema.NullOr(StripeBalanceTransaction),
        refunded: Schema.Boolean
    }),
    bundleId: Schema.NullishOr(Schema.String)
})));
export const PreProcessedPaymentIntentType = StripePaymentIntentType.pipe(Schema.extend(Schema.Struct({
    subscription: PreProcessedSubscriptionType
})));
export const PreProcessedInvoiceType = StripeInvoiceType.omit("subscription").pipe(Schema.extend(Schema.Struct({
    bundleId: Schema.NullishOr(Schema.String),
    subscription: Schema.NullOr(PreProcessedSubscriptionType)
})));
export const PreProcessedChargeRefundType = StripeChargeRefundType.pipe(Schema.extend(Schema.Struct({
    bundleId: Schema.NullishOr(Schema.String),
    subscription: Schema.NullOr(PreProcessedSubscriptionType)
})));
export const PreProcessedStripeEventData = Schema.Union(PreProcessedSubscriptionType, PreProcessedInvoiceType, PreProcessedPaymentIntentType, PreProcessedChargeRefundType);
export const PreProcessedStripeEvent = Schema.Struct({
    data: PreProcessedStripeEventData,
    event: BaseStripeEvent
});
export const AllowedStripeEvent = Schema.Union(StripeSubscriptionType, StripeInvoiceType, StripePaymentIntentType);
